import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeftIcon,
  CameraIcon,
  UserIcon,
  BriefcaseIcon,
  InformationCircleIcon,
  PencilIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentIcon,
  PlusIcon,
  DocumentArrowUpIcon,
  ChartBarIcon,
  EyeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';

const EmployeeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('job-details');
  const [isEditingPicture, setIsEditingPicture] = useState(false);
  const [isEditingJobDetails, setIsEditingJobDetails] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [isEditingPayroll, setIsEditingPayroll] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [contactFormData, setContactFormData] = useState({});
  const [payrollFormData, setPayrollFormData] = useState({});
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({
    name: '',
    file: null
  });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [managerNotes, setManagerNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [originalNotes, setOriginalNotes] = useState('');


  const fetchEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/users/${id}`);
      setEmployee(response.data);
      // Initialize edit form data
      setEditFormData({
        location: response.data.location || '',
        accessLevel: response.data.accessLevel || 'EMPLOYEE',
        role: response.data.role || 'EMPLOYEE',
        pin: response.data.pin || '',
        startDate: response.data.startDate ? new Date(response.data.startDate).toISOString().split('T')[0] : '',
        jobRole: response.data.jobRole || '',
        hourlyWage: response.data.hourlyWage || ''
      });
      // Initialize contact form data
      setContactFormData({
        preferredName: response.data.preferredName || '',
        personalEmail: response.data.personalEmail || '',
        mobileNumber: response.data.phone || '',
        emergencyContact: response.data.emergencyContact || ''
      });
      // Initialize payroll form data
      setPayrollFormData({
        legalName: `${response.data.firstName || ''} ${response.data.lastName || ''}`.trim(),
        dateOfBirth: response.data.dateOfBirth ? new Date(response.data.dateOfBirth).toISOString().split('T')[0] : '',
        ssn: response.data.ssn || '',
        homeAddress: response.data.homeAddress || ''
      });
      // Initialize manager notes
      setManagerNotes(response.data.managerNotes || '');
      setOriginalNotes(response.data.managerNotes || '');
    } catch (err) {
      console.error('Error fetching employee:', err);
      setError('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`/api/users/${id}/documents`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      // If the endpoint doesn't exist, set empty array
      if (err.response?.status === 404) {
        setDocuments([]);
      }
      // Don't set error state for this as it's not critical
    }
  }, [id]);

  const fetchPerformanceData = useCallback(async () => {
    try {
      const response = await axios.get(`/api/users/${id}/performance`);
      setPerformanceData(response.data);
    } catch (err) {
      console.error('Error fetching performance data:', err);
      // If the endpoint doesn't exist, set mock data for demo
      setPerformanceData({
        avgHoursPerWeek: 35.5,
        shiftsPerWeek: 4.2,
        totalShifts: 127,
        onTimePercentage: 94.2,
        overtimeHours: 12.5,
        productivityScore: 87.3
      });
    }
  }, [id]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      await fetchEmployee();
      await fetchDocuments();
      await fetchPerformanceData();
    };
    loadData();
  }, [id, fetchEmployee, fetchDocuments, fetchPerformanceData]);

  const handlePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // For now, just simulate upload - you'll need to implement actual file upload
    console.log('Uploading picture:', file);
    // TODO: Implement actual picture upload to server
    alert('Picture upload functionality to be implemented');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Simple phone formatting - you can enhance this
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  const handleEditJobDetails = () => {
    setIsEditingJobDetails(true);
  };

  const handleSaveJobDetails = async () => {
    try {
      await axios.put(`/api/users/${id}`, editFormData);
      // Update local employee state
      setEmployee(prev => ({
        ...prev,
        ...editFormData
      }));
      setIsEditingJobDetails(false);
    } catch (err) {
      console.error('Error updating employee:', err);
      alert('Failed to update employee details');
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to original values
    setEditFormData({
      location: employee.location || '',
      accessLevel: employee.accessLevel || 'EMPLOYEE',
      role: employee.role || 'EMPLOYEE',
      pin: employee.pin || '',
      startDate: employee.startDate ? new Date(employee.startDate).toISOString().split('T')[0] : '',
      jobRole: employee.jobRole || '',
      hourlyWage: employee.hourlyWage || ''
    });
    setIsEditingJobDetails(false);
  };

  // Contact Information handlers
  const handleEditContact = () => {
    setIsEditingContact(true);
  };

  const handleSaveContact = async () => {
    try {
      await axios.put(`/api/users/${id}`, contactFormData);
      // Update local employee state
      setEmployee(prev => ({
        ...prev,
        ...contactFormData,
        phone: contactFormData.mobileNumber // Map mobileNumber to phone field
      }));
      setIsEditingContact(false);
    } catch (err) {
      console.error('Error updating contact information:', err);
      alert('Failed to update contact information');
    }
  };

  const handleCancelContactEdit = () => {
    // Reset contact form data to original values
    setContactFormData({
      preferredName: employee.preferredName || '',
      personalEmail: employee.personalEmail || '',
      mobileNumber: employee.phone || '',
      emergencyContact: employee.emergencyContact || ''
    });
    setIsEditingContact(false);
  };

  // Payroll Information handlers
  const handleEditPayroll = () => {
    setIsEditingPayroll(true);
  };

  const handleSavePayroll = async () => {
    try {
      // Split legal name back to firstName and lastName
      const nameParts = payrollFormData.legalName.split(' ');
      const updateData = {
        ...payrollFormData,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || ''
      };
      await axios.put(`/api/users/${id}`, updateData);
      // Update local employee state
      setEmployee(prev => ({
        ...prev,
        ...updateData
      }));
      setIsEditingPayroll(false);
    } catch (err) {
      console.error('Error updating payroll information:', err);
      alert('Failed to update payroll information');
    }
  };

  const handleCancelPayrollEdit = () => {
    // Reset payroll form data to original values
    setPayrollFormData({
      legalName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
      dateOfBirth: employee.dateOfBirth ? new Date(employee.dateOfBirth).toISOString().split('T')[0] : '',
      ssn: employee.ssn || '',
      homeAddress: employee.homeAddress || ''
    });
    setIsEditingPayroll(false);
  };

  // Document handlers
  const handleAddDocument = () => {
    setShowDocumentModal(true);
    setDocumentFormData({ name: '', file: null });
  };

  const handleDocumentFileChange = (event) => {
    const file = event.target.files[0];
    console.log('File selected:', file);
    setDocumentFormData({ ...documentFormData, file });
  };

  const handleSaveDocument = async () => {
    if (!documentFormData.name.trim() || !documentFormData.file) {
      alert('Please provide both document name and file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', documentFormData.name);
      formData.append('file', documentFormData.file);
      formData.append('employeeId', id);

      console.log('Uploading document:', {
        name: documentFormData.name,
        fileName: documentFormData.file.name,
        fileSize: documentFormData.file.size,
        employeeId: id
      });

      const response = await axios.post(`/api/users/${id}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Document uploaded successfully:', response.data);
      
      setShowDocumentModal(false);
      setDocumentFormData({ name: '', file: null });
      await fetchDocuments(); // Refresh documents list
    } catch (err) {
      console.error('Error uploading document:', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        alert(`Failed to upload document: ${err.response.data.message || err.response.statusText}`);
      } else if (err.request) {
        console.error('No response received:', err.request);
        alert('Failed to upload document: No response from server');
      } else {
        console.error('Error message:', err.message);
        alert(`Failed to upload document: ${err.message}`);
      }
    }
  };

  const handleCancelDocumentUpload = () => {
    setShowDocumentModal(false);
    setDocumentFormData({ name: '', file: null });
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await axios.delete(`/api/users/${id}/documents/${documentId}`);
      fetchDocuments(); // Refresh documents list
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Failed to delete document');
    }
  };

  const handlePreviewDocument = async (document) => {
    try {
      // Fetch the file as a blob with authentication
      const token = localStorage.getItem('token');
      const previewUrl = getPreviewUrl(document);
      
      const response = await fetch(previewUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load document: ${response.status}`);
      }
      
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      // Add the object URL to the document for preview
      const documentWithObjectUrl = {
        ...document,
        objectUrl: objectUrl
      };
      
      setPreviewDocument(documentWithObjectUrl);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error loading document for preview:', error);
      alert('Failed to load document for preview. Please try downloading it instead.');
    }
  };

  const handleClosePreview = () => {
    // Clean up object URL to prevent memory leaks
    if (previewDocument && previewDocument.objectUrl) {
      URL.revokeObjectURL(previewDocument.objectUrl);
    }
    setShowPreviewModal(false);
    setPreviewDocument(null);
  };

  const isPreviewable = (mimeType) => {
    return [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf'
    ].includes(mimeType);
  };

  const getPreviewUrl = (document) => {
    // Add preview=true query parameter to the download URL
    return `${document.fileUrl}?preview=true`;
  };

  const renderPreviewContent = () => {
    if (!previewDocument) return null;

    const { mimeType, fileUrl, fileName, objectUrl } = previewDocument;
    
    if (mimeType.startsWith('image/')) {
      return (
        <img 
          src={objectUrl} 
          alt={fileName}
          className="max-w-full max-h-[70vh] object-contain mx-auto"
          onError={(e) => {
            console.error('Image failed to load:', e);
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
      );
    } else if (mimeType === 'application/pdf') {
      return (
        <iframe
          src={objectUrl}
          className="w-full h-[70vh] border-0"
          title={fileName}
          onError={(e) => {
            console.error('PDF failed to load:', e);
          }}
        >
          <p>Your browser does not support PDFs. <a href={fileUrl} target="_blank" rel="noopener noreferrer">Download the PDF</a>.</p>
        </iframe>
      );
    }
    
    return (
      <div className="text-center py-8">
        <DocumentIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Preview not available for this file type.</p>
        <a 
          href={fileUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline mt-2 inline-block"
        >
          Open in new tab
        </a>
      </div>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Manager Notes handlers
  const handleEditNotes = () => {
    setIsEditingNotes(true);
    setOriginalNotes(managerNotes);
  };

  const handleSaveNotes = async () => {
    try {
      await axios.put(`/api/users/${id}`, { managerNotes });
      // Update local employee state
      setEmployee(prev => ({
        ...prev,
        managerNotes
      }));
      setIsEditingNotes(false);
      setOriginalNotes(managerNotes);
    } catch (err) {
      console.error('Error updating manager notes:', err);
      alert('Failed to update manager notes');
    }
  };

  const handleCancelNotesEdit = () => {
    setManagerNotes(originalNotes);
    setIsEditingNotes(false);
  };

  const calculateMilestones = (startDate) => {
    if (!startDate) return [];
    
    const hireDate = new Date(startDate);
    const today = new Date();
    const milestones = [];
    
    // Calculate months and years since hire
    const monthsDiff = (today.getFullYear() - hireDate.getFullYear()) * 12 + (today.getMonth() - hireDate.getMonth());
    const yearsDiff = Math.floor(monthsDiff / 12);
    
    // Add hire date
    milestones.push({
      date: hireDate,
      title: 'Hired',
      type: 'hire',
      achieved: true
    });
    
    // 3 months milestone
    const threeMonths = new Date(hireDate);
    threeMonths.setMonth(threeMonths.getMonth() + 3);
    milestones.push({
      date: threeMonths,
      title: '3 Month Anniversary',
      type: 'milestone',
      achieved: monthsDiff >= 3
    });
    
    // 6 months milestone
    const sixMonths = new Date(hireDate);
    sixMonths.setMonth(sixMonths.getMonth() + 6);
    milestones.push({
      date: sixMonths,
      title: '6 Month Anniversary',
      type: 'milestone',
      achieved: monthsDiff >= 6
    });
    
    // 1 year milestone
    const oneYear = new Date(hireDate);
    oneYear.setFullYear(oneYear.getFullYear() + 1);
    milestones.push({
      date: oneYear,
      title: '1 Year Anniversary',
      type: 'anniversary',
      achieved: yearsDiff >= 1
    });
    
    // 2+ year milestones
    for (let year = 2; year <= Math.max(yearsDiff + 1, 2); year++) {
      const anniversary = new Date(hireDate);
      anniversary.setFullYear(anniversary.getFullYear() + year);
      milestones.push({
        date: anniversary,
        title: `${year} Year Anniversary`,
        type: 'anniversary',
        achieved: yearsDiff >= year
      });
    }
    
    return milestones.sort((a, b) => a.date - b.date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <EmptyState
        icon={UserIcon}
        title="Employee Not Found"
        description={error || "The employee you're looking for doesn't exist."}
        action={{
          label: 'Back to Employees',
          onClick: () => navigate('/employees')
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/employees')}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Employees
        </button>
      </div>

      {/* Profile Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start space-x-6">
          {/* Profile Picture */}
          <div className="relative">
            <div className="h-24 w-24 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
              {employee.profilePicture ? (
                <img 
                  src={employee.profilePicture} 
                  alt={`${employee.firstName} ${employee.lastName}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-2xl font-bold text-gray-700">
                  {employee.firstName.charAt(0)}{employee.lastName.charAt(0)}
                </span>
              )}
            </div>
            
            {/* Upload Picture Button */}
            <button
              onClick={() => setIsEditingPicture(true)}
              className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2"
              title="Upload picture"
            >
              <CameraIcon className="h-4 w-4" />
            </button>

            {/* Hidden file input */}
            {isEditingPicture && (
              <input
                type="file"
                accept="image/*"
                onChange={handlePictureUpload}
                onBlur={() => setIsEditingPicture(false)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                autoFocus
              />
            )}
          </div>

          {/* Employee Info */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {employee.firstName} {employee.lastName}
            </h1>
            <p className="text-lg text-gray-600 mt-1">
              {employee.jobRole || 'Employee'} at {employee.organization?.name || 'Company'}
            </p>
            <div className="flex items-center mt-2 text-gray-500">
              <span>{formatPhoneNumber(employee.phone) || 'No phone'}</span>
              <span className="mx-2">|</span>
              <span>{employee.email}</span>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center space-x-3 mt-4">
              <button
                onClick={() => navigate(`/employees/${id}/edit`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full flex items-center text-sm font-medium"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Employee
              </button>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                employee.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {employee.isActive ? 'Active' : 'Terminated'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto scrollbar-hide" aria-label="Tabs" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            <button
              onClick={() => setActiveTab('job-details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'job-details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BriefcaseIcon className="h-5 w-5 mr-2 inline" />
              Job Details
            </button>
            <button
              onClick={() => setActiveTab('personal-info')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'personal-info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <InformationCircleIcon className="h-5 w-5 mr-2 inline" />
              Personal Information
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'documents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <DocumentIcon className="h-5 w-5 mr-2 inline" />
              Documents
            </button>
            <button
              onClick={() => setActiveTab('performance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'performance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="h-5 w-5 mr-2 inline" />
              Performance
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Job Details Tab */}
          {activeTab === 'job-details' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Job Details</h3>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex-1">
                    {isEditingJobDetails ? (
                      <input
                        type="text"
                        value={editFormData.location}
                        onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                        className="text-xl font-bold text-gray-900 border border-gray-300 rounded px-3 py-1 w-full max-w-xs"
                        placeholder="Location"
                      />
                    ) : (
                      <h4 className="text-xl font-bold text-gray-900">{employee.location || 'Location Not Set'}</h4>
                    )}
                  </div>
                  {isEditingJobDetails ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveJobDetails}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleEditJobDetails}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Access Level</label>
                    {isEditingJobDetails ? (
                      <select
                        value={editFormData.role === 'BUSINESS_ADMIN' ? 'Administrator' : editFormData.accessLevel === 'MANAGER' ? 'Administrator' : 'Employee'}
                        onChange={(e) => {
                          if (e.target.value === 'Administrator') {
                            setEditFormData({...editFormData, accessLevel: 'MANAGER'});
                          } else {
                            setEditFormData({...editFormData, accessLevel: 'EMPLOYEE'});
                          }
                        }}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Employee">Employee</option>
                        <option value="Administrator">Administrator</option>
                      </select>
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.role === 'BUSINESS_ADMIN' ? 'Administrator' : 
                         employee.accessLevel === 'MANAGER' ? 'Administrator' : 
                         'Employee'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pin</label>
                    {isEditingJobDetails ? (
                      <input
                        type="text"
                        value={editFormData.pin}
                        onChange={(e) => setEditFormData({...editFormData, pin: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="4 digit pin"
                        maxLength="4"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.pin ? '••••' : 'Employee can set their own 4 digit pin'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    {isEditingJobDetails ? (
                      <input
                        type="date"
                        value={editFormData.startDate}
                        onChange={(e) => setEditFormData({...editFormData, startDate: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{formatDate(employee.startDate)}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    {isEditingJobDetails ? (
                      <input
                        type="text"
                        value={editFormData.jobRole}
                        onChange={(e) => setEditFormData({...editFormData, jobRole: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Job role"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{employee.jobRole || 'Not assigned'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Wage</label>
                    {isEditingJobDetails ? (
                      <div className="mt-1 flex items-center">
                        <span className="text-sm text-gray-500 mr-2">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editFormData.hourlyWage}
                          onChange={(e) => setEditFormData({...editFormData, hourlyWage: e.target.value})}
                          className="text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                        <span className="text-sm text-gray-500 ml-2">/hr</span>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.hourlyWage ? `$${parseFloat(employee.hourlyWage).toFixed(2)}/hr` : 'Not specified'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Job History Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <CalendarIcon className="h-5 w-5 text-gray-500 mr-2" />
                  <h4 className="text-lg font-medium text-gray-900">Job History</h4>
                </div>

                {employee.startDate ? (
                  <div className="space-y-4">
                    {calculateMilestones(employee.startDate).map((milestone, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          milestone.achieved 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          {milestone.achieved ? (
                            <CheckCircleIcon className="h-5 w-5" />
                          ) : (
                            <ClockIcon className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${
                            milestone.achieved ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {milestone.title}
                          </div>
                          <div className={`text-xs ${
                            milestone.achieved ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {milestone.date.toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        {milestone.achieved && (
                          <div className="text-xs text-green-600 font-medium">
                            Completed
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No Start Date</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Set a start date to view employment milestones.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Personal Information Tab */}
          {activeTab === 'personal-info' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
              </div>

              {/* Contact Information Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-lg font-medium text-gray-900">Contact Information</h4>
                  {isEditingContact ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveContact}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelContactEdit}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleEditContact}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Preferred Name</label>
                    {isEditingContact ? (
                      <input
                        type="text"
                        value={contactFormData.preferredName}
                        onChange={(e) => setContactFormData({...contactFormData, preferredName: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Preferred name"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{employee.preferredName || 'Not specified'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Personal Email</label>
                    {isEditingContact ? (
                      <input
                        type="email"
                        value={contactFormData.personalEmail}
                        onChange={(e) => setContactFormData({...contactFormData, personalEmail: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="personal@email.com"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{employee.personalEmail || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                    {isEditingContact ? (
                      <input
                        type="tel"
                        value={contactFormData.mobileNumber}
                        onChange={(e) => setContactFormData({...contactFormData, mobileNumber: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="(555) 123-4567"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{formatPhoneNumber(employee.phone) || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Emergency Contact</label>
                    {isEditingContact ? (
                      <input
                        type="text"
                        value={contactFormData.emergencyContact}
                        onChange={(e) => setContactFormData({...contactFormData, emergencyContact: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Emergency contact info"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{employee.emergencyContact || 'Not provided'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payroll Information Card */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-lg font-medium text-gray-900">Payroll Information</h4>
                  {isEditingPayroll ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSavePayroll}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelPayrollEdit}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleEditPayroll}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Legal Name</label>
                    {isEditingPayroll ? (
                      <input
                        type="text"
                        value={payrollFormData.legalName}
                        onChange={(e) => setPayrollFormData({...payrollFormData, legalName: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Full legal name"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{employee.firstName} {employee.lastName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    {isEditingPayroll ? (
                      <input
                        type="date"
                        value={payrollFormData.dateOfBirth}
                        onChange={(e) => setPayrollFormData({...payrollFormData, dateOfBirth: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{formatDate(employee.dateOfBirth) || 'Not provided'}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Social Security Number</label>
                    {isEditingPayroll ? (
                      <input
                        type="text"
                        value={payrollFormData.ssn}
                        onChange={(e) => setPayrollFormData({...payrollFormData, ssn: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="XXX-XX-XXXX"
                        maxLength="11"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">
                        {employee.ssn ? `***-**-${employee.ssn.slice(-4)}` : 'Not provided'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Home Address</label>
                    {isEditingPayroll ? (
                      <textarea
                        value={payrollFormData.homeAddress}
                        onChange={(e) => setPayrollFormData({...payrollFormData, homeAddress: e.target.value})}
                        className="mt-1 text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Home address"
                        rows="2"
                      />
                    ) : (
                      <p className="mt-1 text-sm text-gray-900">{employee.homeAddress || 'Not provided'}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Documents</h3>
                <button
                  onClick={handleAddDocument}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Document
                </button>
              </div>

              {/* Documents List */}
              <div className="bg-white border border-gray-200 rounded-lg">
                {documents.length === 0 ? (
                  <div className="text-center py-12">
                    <DocumentIcon className="mx-auto h-16 w-16 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Documents</h3>
                    <p className="mt-2 text-gray-500">
                      No documents have been uploaded for this employee yet.
                    </p>
                    <button
                      onClick={handleAddDocument}
                      className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center mx-auto"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add First Document
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {documents.map((document, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <DocumentIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">{document.name}</h4>
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                <span>{document.fileName}</span>
                                <span>•</span>
                                <span>{formatFileSize(document.fileSize)}</span>
                                <span>•</span>
                                <span>Uploaded {formatDate(document.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {isPreviewable(document.mimeType) && (
                              <button
                                onClick={() => handlePreviewDocument(document)}
                                className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center"
                                title="Preview document"
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                Preview
                              </button>
                            )}
                            <a
                              href={document.fileUrl}
                              download={document.fileName}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Download
                            </a>
                            <button
                              onClick={() => handleDeleteDocument(document.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Performance</h3>
              </div>

              {/* Performance Statistics */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Performance Statistics</h4>
                
                {performanceData ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <ClockIcon className="h-8 w-8 text-blue-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Average Hours/Week</p>
                          <p className="text-2xl font-bold text-blue-900">{performanceData.avgHoursPerWeek}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <CalendarIcon className="h-8 w-8 text-green-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-green-800">Shifts per Week</p>
                          <p className="text-2xl font-bold text-green-900">{performanceData.shiftsPerWeek}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <ChartBarIcon className="h-8 w-8 text-purple-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-purple-800">Total Shifts</p>
                          <p className="text-2xl font-bold text-purple-900">{performanceData.totalShifts}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-8 w-8 text-yellow-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-yellow-800">On-Time %</p>
                          <p className="text-2xl font-bold text-yellow-900">{performanceData.onTimePercentage}%</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <ClockIcon className="h-8 w-8 text-red-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Overtime Hours</p>
                          <p className="text-2xl font-bold text-red-900">{performanceData.overtimeHours}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-indigo-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <ChartBarIcon className="h-8 w-8 text-indigo-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-indigo-800">Productivity Score</p>
                          <p className="text-2xl font-bold text-indigo-900">{performanceData.productivityScore}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Loading Performance Data</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Performance statistics are being loaded...
                    </p>
                  </div>
                )}
              </div>

              {/* Manager Notes */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Manager Notes</h4>
                  {isEditingNotes ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveNotes}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelNotesEdit}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleEditNotes}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center text-sm"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                  )}
                </div>

                {isEditingNotes ? (
                  <textarea
                    value={managerNotes}
                    onChange={(e) => setManagerNotes(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
                    placeholder="Add manager notes about this employee's performance, behavior, goals, etc."
                  />
                ) : (
                  <div className="min-h-[120px] p-3 bg-gray-50 rounded-lg">
                    {managerNotes ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{managerNotes}</p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No manager notes added yet. Click "Edit" to add notes about this employee.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Document Upload Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Add Document</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Name
                </label>
                <input
                  type="text"
                  value={documentFormData.name}
                  onChange={(e) => setDocumentFormData({...documentFormData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter document name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    onChange={handleDocumentFileChange}
                    className="hidden"
                    id="document-file-input"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  />
                  <label
                    htmlFor="document-file-input"
                    className="cursor-pointer"
                  >
                    <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-2">
                      <span className="text-sm font-medium text-blue-600 hover:text-blue-500">
                        Click to upload
                      </span>
                      <span className="text-sm text-gray-500"> or drag and drop</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      PDF, DOC, DOCX, JPG, PNG, TXT up to 10MB
                    </p>
                  </label>
                  
                  {documentFormData.file && (
                    <div className="mt-2 text-sm text-gray-600">
                      Selected: {documentFormData.file.name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleCancelDocumentUpload}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDocument}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium"
                disabled={!documentFormData.name.trim() || !documentFormData.file}
              >
                Upload Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {showPreviewModal && previewDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{previewDocument.name}</h2>
                <p className="text-sm text-gray-500">{previewDocument.fileName}</p>
              </div>
              <div className="flex items-center space-x-2">
                <a
                  href={previewDocument.fileUrl}
                  download={previewDocument.fileName}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 border border-blue-600 rounded"
                >
                  Download
                </a>
                <button
                  onClick={handleClosePreview}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              {renderPreviewContent()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDetails;